import { twMerge } from 'tailwind-merge';
import { clsx, type ClassValue } from 'clsx';

import type { SourceTableEntry } from '@/types/api';

/**
 * Extracts an error message from an unknown caught value.
 *
 * @param { unknown } err The caught error value.
 * @param { string } fallback Fallback message when err is not an Error instance.
 * @returns { string } The error message string.
 */
export const getErrorMessage = (err: unknown, fallback = 'Unknown error'): string =>
  err instanceof Error ? err.message : fallback;

/**
 * Merges and deduplicates CSS class names using clsx and tailwind-merge.
 *
 * @param { ClassValue[] } inputs Variable number of class value inputs.
 * @returns { string } The merged class name string.
 */
export const cn = (...inputs: ClassValue[]) => twMerge(clsx(inputs));

/**
 * Extracts the primary table name from a sourceTables array.
 * UI-safe variant that returns an empty string instead of throwing on malformed data.
 *
 * @param { SourceTableEntry[] } sourceTables The source tables array.
 * @returns { string } The primary table name, or the first table, or empty string.
 */
export const getPrimaryTableName = (sourceTables: SourceTableEntry[]): string => {
  const primary = sourceTables.find((s) => s.isPrimary);

  return primary?.table ?? sourceTables[0]?.table ?? '';
};

/**
 * Derives a deterministic mapping key from source table names.
 * Single-table: returns the table name. Multi-table: sorted, ~-joined.
 *
 * Key derivation is duplicated in SQL (COLLATE "C"), Node.js (.sort()), and UI (.sort()).
 * All three assume ASCII-only table names to guarantee identical sort order.
 * See: Mobile POS SQL/procedures/shadow_table_procedures.sql, source-table-helpers.ts,
 * SourceTableEntrySchema validation in admin-service/server/modules/mappings/types.ts.
 *
 * @param { string[] } tables The source table names.
 * @returns { string } The derived mapping key.
 */
export const getSourceTablesKey = (tables: string[]): string => {
  if (tables.length === 1) {
    return tables[0] ?? '';
  }

  return [...tables].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0)).join('~');
};

/**
 * Builds the label that names one mapping to the user: its mapping key and, when it has one, the code of
 * its adapter configuration. One table set can have several mappings, so the key alone is ambiguous.
 *
 * @param { string } mappingKey The mapping key (the source table set).
 * @param { string | undefined } adapterConfigCode The code of the mapping's adapter configuration, if known.
 * @returns { string } The label, e.g. `orders (ERP_DB)`.
 */
export const formatMappingLabel = (mappingKey: string, adapterConfigCode: string | undefined): string =>
  adapterConfigCode ? `${mappingKey} (${adapterConfigCode})` : mappingKey;

/**
 * Builds a sort comparison that orders mappings by mapping key, then by adapter configuration code, then by
 * id. One table set can have several mappings, one per adapter configuration, so this keeps them together.
 *
 * @param { function } getCode Returns a mapping's adapter configuration code, or '' when it has none.
 * @returns { function } Returns the comparison function for `Array.prototype.sort`.
 */
export const compareMappings =
  <T extends { id: number; mappingKey: string }>(getCode: (mapping: T) => string) =>
  (a: T, b: T): number =>
    a.mappingKey.localeCompare(b.mappingKey) || getCode(a).localeCompare(getCode(b)) || a.id - b.id;
